import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, LOCALE_ID } from '@angular/core';
import { NbSecurityModule, NbRoleProvider } from '@nebular/security';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { RoutingModule } from './app-routing.module';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from './shared/shared.module';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HeaderComponent } from './layout/header/header.component';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { Globals } from './core/Globals';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { RequestCacheService } from './shared/services/cache.service';
import { registerLocaleData } from '@angular/common';
import { MaterialModule } from './material/material.module';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NbAuthModule } from '@nebular/auth';
import { MaintenanceComponent } from './shared/maintenance/maintenance.component';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { AuthModule } from './auth/auth.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SideMenuComponent } from './layout/side-menu/side-menu.component';
import { NbLayoutModule, NbThemeModule, NbSidebarService, NbWindowModule } from '@nebular/theme';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CourseService } from './shared/services/training/course.service';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { environment } from '../environments/environment';
import { OAuthModule } from 'angular-oauth2-oidc';
import { FooterComponent } from './layout/footer/footer.component';
import { RoleProvider } from './auth/services/role-provider.service';
import { LocaleSelectorComponent } from './layout/header/locale-selector/locale-selector.component';
import { HeaderChoicesComponent } from './layout/header/header-choices/header-choices.component';

const localeName = navigator && navigator.language ? navigator.language.substring(0, 2) : 'en';

import(`@angular/common/locales/${localeName}.js`).then(locale => {
  registerLocaleData(locale.default);
});

export const httpLoaderFactory = (httpClient: HttpClient) => new TranslateHttpLoader(httpClient, './assets/i18n/', '.json?v=' + Date.now());

// AoT requires an exported function for factories
/**
 * App Module
 */
@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SideMenuComponent,
    FooterComponent,
    LocaleSelectorComponent,
    HeaderChoicesComponent,
    MaintenanceComponent,
  ],
  imports: [
    BrowserModule,
    RoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    NgbModule,
    MatDialogModule,
    NbEvaIconsModule,
    NbLayoutModule,
    NgxMaterialTimepickerModule,
    NbAuthModule.forRoot(),
    NbThemeModule.forRoot(),
    CoreModule.forRoot(),
    AuthModule.forRoot(),
    SharedModule.forRoot(),
    ToastrModule.forRoot({
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true,
      enableHtml: true,
    }),
    NgxMaterialTimepickerModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    IconSpriteModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NbWindowModule.forRoot(),
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [
          environment.exchangeApi.url,
          environment.mediaUrl,
        ],
        sendAccessToken: true,
      },
    }),
    NbSecurityModule.forRoot({
      accessControl: {
        guest: {
          view: ['booking'],
        },
        internalMember: {
          parent: 'guest',
        },
        externalMember: {
          view: [],
        },
      },
    }),
    SharedModule,
  ],
  exports: [
    MaintenanceComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  providers: [
    Globals,
    RequestCacheService,
    NbSidebarService,
    {provide: LOCALE_ID, useValue: navigator.language},
    {provide: MAT_DIALOG_DATA, useValue: {}},
    // for services debug
    CourseService,
    { provide: NbRoleProvider, useClass: RoleProvider },
  ],
})
export class AppModule {
}
